import { useEffect, useMemo, useState } from 'react';
import { Select, Space, Typography } from 'antd';
import { useTopics } from '@/hooks/useTopics';
import { readLinksOfType } from '@/types/topic';
import type { Topic, TopicDetails } from '@/types/topic';

const IMG_RE = /\.(png|jpe?g|webp)(\?.*)?$/i;

export interface ProgramPick { topicNo: string; promptSeed: string; images: string[] }

function titleOf(d: TopicDetails | undefined): string {
  const t = d?.title ?? {};
  return t.en ?? t['zh-Hans'] ?? Object.values(t)[0] ?? '(untitled)';
}

function pickOf(t: Topic): ProgramPick {
  const d = t.details ?? {};
  const title = titleOf(d);
  const text = d.content?.text ?? {};
  const body = (text.en ?? Object.values(text)[0] ?? '').slice(0, 200);
  const links = [...readLinksOfType(d, 'flyer'), ...readLinksOfType(d, 'attachment')];
  const images = [...new Set(links.map((l) => l.url).filter((u) => IMG_RE.test(u)))];
  return {
    topicNo: String(t.topic_no ?? t.id),
    promptSeed: `Promotional flyer background for the program "${title}". ${body}`.trim(),
    images,
  };
}

export function ProgramRefPicker({ onPick, onClear, initialTopicNo }: {
  onPick: (p: ProgramPick) => void;
  onClear?: () => void;
  initialTopicNo?: string;
}) {
  const topicsQ = useTopics({ status: 'active' });
  const [value, setValue] = useState<string | undefined>();
  const options = useMemo(
    () => (topicsQ.data ?? []).map((t) => ({ value: t.id, label: titleOf(t.details) })),
    [topicsQ.data],
  );

  // Pre-bound program: show it in the Select (display only — don't fire
  // onPick, which would clobber prompt/reference-image state on mount).
  useEffect(() => {
    if (value !== undefined || !initialTopicNo) return;
    const t = (topicsQ.data ?? []).find((x) => String(x.topic_no ?? x.id) === initialTopicNo);
    if (t) setValue(t.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicsQ.data, initialTopicNo]);

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={2}>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>Program (optional — prefills prompt + reference images)</Typography.Text>
      <Select
        style={{ width: '100%' }} showSearch allowClear placeholder="Pick a program…"
        loading={topicsQ.isLoading} value={value} options={options}
        filterOption={(input, o) => String(o?.label ?? '').toLowerCase().includes(input.toLowerCase())}
        onChange={(id) => {
          setValue(id);
          if (id == null) { onClear?.(); return; }
          const t = (topicsQ.data ?? []).find((x) => x.id === id);
          if (t) onPick(pickOf(t));
        }}
      />
    </Space>
  );
}
